import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { api, setToken } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import BuzzLogo from "../components/BuzzLogo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) setError("This reset link is missing its token. Request a new one.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("The two passwords don't match.");
    setBusy(true);
    try {
      const r = await api.post("/auth/reset", { token, password });
      // Auto-log-them-in with the fresh token the server returned
      if (r.data.token) {
        setToken(r.data.token);
        await refresh();
      }
      toast.success("Password updated. Welcome back.");
      nav("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed. Please request a new link.");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center px-6 font-body">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 rounded-lg bg-[#0E1512] flex items-center justify-center"><BuzzLogo size={20} /></div>
          <span className="font-heading font-extrabold">BuzzerAI</span>
        </Link>
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#D96C5B] font-semibold mb-2">New password</div>
        <h1 className="font-heading text-4xl md:text-5xl text-[#0E1512] tracking-tight leading-none mb-3">Pick a fresh one.</h1>
        <p className="text-sm text-[#5C6B61] mb-8">Once you save, you're back in — no need to log in again.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div>
            <label className="overline block mb-1.5">New password (8+ chars)</label>
            <input
              data-testid="reset-password"
              className="input-field"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
              autoComplete="new-password"
            />
          </div>
          <div>
            <label className="overline block mb-1.5">Confirm password</label>
            <input
              data-testid="reset-confirm"
              className="input-field"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={8}
              required
              autoComplete="new-password"
            />
          </div>
          {error && <div className="text-sm text-[#A5382A] bg-[#FEF2F0] px-3 py-2 rounded" data-testid="reset-error">{error}</div>}
          <button
            data-testid="reset-submit"
            disabled={busy || !token}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#0E1512] text-white text-sm font-bold px-6 py-3.5 rounded-full hover:bg-[#1E3A2F] transition-all disabled:opacity-60"
          >
            {busy ? "Saving…" : "Save new password"} <ArrowRight size={16} weight="bold" />
          </button>
        </form>
        <p className="mt-6 text-sm text-[#5C6B61]">
          Changed your mind? <Link to="/login" className="text-[#121914] font-semibold underline decoration-[#F5C543] decoration-2 underline-offset-4">Back to log in</Link>
        </p>
      </div>
    </div>
  );
}
